import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Star, Users, ClipboardList, TrendingUp, Plus, X, Copy, Trash2,
  Edit3, Loader2, Search, Check, ChevronDown, ChevronUp,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../lib/api';
import StarRating from '../components/StarRating';

export default function TeacherFeedbackAdmin() {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [forms, setForms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [subFilters, setSubFilters] = useState({ form_id: '', teacher_id: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [teacherDrilldown, setTeacherDrilldown] = useState(null); // {summary, comments}

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, f, c, t] = await Promise.all([
        api.getFeedbackAnalytics(),
        api.getFeedbackForms(),
        api.getCoursesList(),
        api.getTeachers(),
      ]);
      setAnalytics(a.data);
      setForms(f.data || []);
      setCourses(c.data || []);
      setTeachers(t.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  const fetchSubs = async () => {
    try {
      const params = {};
      if (subFilters.form_id) params.form_id = subFilters.form_id;
      if (subFilters.teacher_id) params.teacher_id = subFilters.teacher_id;
      const res = await api.getFeedbackSubmissions(params);
      setSubmissions(res.data || []);
    } catch (e) {
      toast.error('Failed to load submissions');
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (tab === 'submissions') fetchSubs(); }, [tab, subFilters]);

  const publicLink = (slug) => `${window.location.origin}/feedback/${slug}`;
  const copyLink = async (slug) => {
    try { await navigator.clipboard.writeText(publicLink(slug)); toast.success('Link copied'); }
    catch (e) { toast.error('Copy failed'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this feedback form?')) return;
    try { await api.deleteFeedbackForm(id); toast.success('Form deactivated'); fetchAll(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const openTeacher = async (teacherId) => {
    try {
      const res = await api.getFeedbackTeacherDeepDive(teacherId);
      setTeacherDrilldown(res.data);
    } catch (e) { toast.error('Cannot load teacher analytics'); }
  };

  const overallChartData = useMemo(() => {
    if (!analytics?.overall_distribution) return [];
    return [1, 2, 3, 4, 5].map((n) => ({
      rating: `${n}★`,
      count: analytics.overall_distribution[String(n)] || 0,
    }));
  }, [analytics]);

  const barColors = ['#dc2626', '#f97316', '#eab308', '#84cc16', '#10b981'];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  }

  const statCards = [
    { label: 'Active Forms', value: analytics?.total_forms || 0, icon: ClipboardList, color: 'from-blue-500 to-blue-700' },
    { label: 'Submissions', value: analytics?.total_submissions || 0, icon: Users, color: 'from-indigo-500 to-indigo-700' },
    { label: 'Total Ratings', value: analytics?.total_ratings || 0, icon: Star, color: 'from-amber-500 to-amber-700' },
    { label: 'Avg Rating', value: (analytics?.overall_avg_rating || 0).toFixed(2), icon: TrendingUp, color: 'from-emerald-500 to-emerald-700' },
  ];

  return (
    <div className="max-w-[1600px] mx-auto space-y-6 px-2 sm:px-0" data-testid="teacher-feedback-admin">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">Teacher Feedback</h1>
          <p className="text-sm text-slate-600 mt-1">Batch-wise feedback forms with per-teacher analytics.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          data-testid="feedback-create-btn"
        >
          <Plus size={18} /> New Feedback Form
        </button>
      </div>

      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'forms', label: `Forms (${forms.length})` },
          { id: 'analytics', label: 'Teacher Analytics' },
          { id: 'submissions', label: 'Submissions' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`feedback-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.id ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {statCards.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className={`rounded-xl p-4 bg-gradient-to-br ${s.color} text-white shadow-lg`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wider opacity-80">{s.label}</div>
                      <div className="text-3xl font-bold mt-1">{s.value}</div>
                    </div>
                    <Icon size={32} className="opacity-40" />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="font-semibold text-slate-800 mb-3">Overall Rating Distribution</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={overallChartData}>
                  <XAxis dataKey="rating" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {overallChartData.map((_, idx) => <Cell key={idx} fill={barColors[idx]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="font-semibold text-slate-800 mb-3">Top Teachers by Rating</div>
              {(analytics?.per_teacher || []).length === 0 ? (
                <div className="text-sm text-slate-500">No ratings yet</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {analytics.per_teacher.slice(0, 10).map((t) => (
                    <button key={t.teacher_id} onClick={() => openTeacher(t.teacher_id)}
                            className="w-full text-left flex items-center justify-between py-2 px-2 hover:bg-slate-50 rounded"
                            data-testid={`feedback-teacher-row-${t.teacher_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{t.teacher_name}</div>
                        <div className="text-xs text-slate-500">{t.total_ratings} ratings</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StarRating value={Math.round(t.avg_rating)} readonly size={14} testIdPrefix={`feedback-stars-${t.teacher_id}`} />
                        <span className="text-sm font-semibold text-amber-600 w-10 text-right">{t.avg_rating}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'forms' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <th className="px-4 py-3">Course (Batch)</th>
                <th className="px-4 py-3">Teachers</th>
                <th className="px-4 py-3">Submissions</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Public Link</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {forms.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500">
                  <ClipboardList size={40} className="mx-auto mb-2 text-slate-300" />
                  No feedback forms yet.
                </td></tr>
              )}
              {forms.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50" data-testid={`feedback-form-row-${f.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{f.course_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    <div className="flex flex-wrap gap-1">
                      {(f.teachers || []).slice(0, 4).map((t) => (
                        <span key={t.id} className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-xs">{t.name}</span>
                      ))}
                      {(f.teachers || []).length > 4 && (
                        <span className="text-xs text-slate-400">+{f.teachers.length - 4}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{f.submission_count}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${f.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                      {f.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded truncate max-w-[220px]">/feedback/{f.slug}</code>
                      <button onClick={() => copyLink(f.slug)} className="p-1 hover:bg-slate-200 rounded"
                              data-testid={`feedback-copy-${f.id}`}>
                        <Copy size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button onClick={() => setEditForm(f)} className="p-2 hover:bg-slate-100 rounded" title="Edit"><Edit3 size={16} /></button>
                    <button onClick={() => handleDelete(f.id)} className="p-2 hover:bg-rose-50 rounded text-rose-600" title="Delete"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {(analytics?.per_teacher || []).length === 0 && (
            <div className="col-span-3 bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500 text-sm">
              No teacher ratings yet.
            </div>
          )}
          {(analytics?.per_teacher || []).map((t) => {
            const data = [1, 2, 3, 4, 5].map((n) => ({ rating: `${n}★`, count: t.distribution[String(n)] || 0 }));
            return (
              <div key={t.teacher_id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-slate-900">{t.teacher_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.total_ratings} ratings</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-amber-600">{t.avg_rating}</div>
                    <StarRating value={Math.round(t.avg_rating)} readonly size={14} testIdPrefix={`stat-star-${t.teacher_id}`} />
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={data}>
                    <XAxis dataKey="rating" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {data.map((_, idx) => <Cell key={idx} fill={barColors[idx]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <button onClick={() => openTeacher(t.teacher_id)}
                        className="mt-2 text-xs text-amber-700 hover:text-amber-900 font-medium"
                        data-testid={`feedback-view-comments-${t.teacher_id}`}>
                  View comments →
                </button>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'submissions' && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select value={subFilters.form_id}
                    onChange={(e) => setSubFilters((s) => ({ ...s, form_id: e.target.value }))}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="feedback-sub-form-filter">
              <option value="">All Forms</option>
              {forms.map((f) => <option key={f.id} value={f.id}>{f.course_name}</option>)}
            </select>
            <select value={subFilters.teacher_id}
                    onChange={(e) => setSubFilters((s) => ({ ...s, teacher_id: e.target.value }))}
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="">All Teachers</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div className="text-sm text-slate-500 flex items-center">Showing {submissions.length} submissions</div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {submissions.length === 0 && (
              <div className="p-10 text-center text-slate-500 text-sm">No submissions</div>
            )}
            {submissions.map((s) => (
              <SubmissionRow key={s.id} sub={s} />
            ))}
          </div>
        </div>
      )}

      {(showCreate || editForm) && (
        <FeedbackFormModal
          courses={courses} teachers={teachers} form={editForm}
          onClose={() => { setShowCreate(false); setEditForm(null); }}
          onSaved={() => { setShowCreate(false); setEditForm(null); fetchAll(); }}
        />
      )}

      {teacherDrilldown && (
        <TeacherDrilldownModal data={teacherDrilldown} onClose={() => setTeacherDrilldown(null)} />
      )}
    </div>
  );
}

function SubmissionRow({ sub }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="p-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">{sub.student_name} <span className="text-slate-400 font-normal">· {sub.student_phone}</span></div>
          <div className="text-xs text-slate-500 truncate">{sub.course_name} · {new Date(sub.created_at).toLocaleString('en-IN')}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{sub.ratings.length} ratings</span>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {sub.ratings.map((r, i) => (
            <div key={i} className="flex items-start justify-between bg-slate-50 rounded-lg p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{r.teacher_name}</div>
                {r.comment && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap break-words">"{r.comment}"</div>}
              </div>
              <StarRating value={r.rating} readonly size={16} testIdPrefix={`sub-${sub.id}-${r.teacher_id}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeacherDrilldownModal({ data, onClose }) {
  const s = data.summary;
  const chartData = s ? [1, 2, 3, 4, 5].map((n) => ({ rating: `${n}★`, count: s.distribution[String(n)] || 0 })) : [];
  const barColors = ['#dc2626', '#f97316', '#eab308', '#84cc16', '#10b981'];
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" data-testid="feedback-drilldown-modal">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">{s?.teacher_name || 'Teacher'}</div>
            <div className="text-xs text-slate-500">{s?.total_ratings || 0} ratings · Avg {s?.avg_rating || 0}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          {s && (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData}>
                <XAxis dataKey="rating" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, idx) => <Cell key={idx} fill={barColors[idx]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <div>
            <div className="text-sm font-semibold text-slate-800 mb-2">Recent Comments</div>
            {(data.comments || []).length === 0 && <div className="text-sm text-slate-500">No comments yet</div>}
            <div className="space-y-2">
              {(data.comments || []).map((c, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-800">{c.student_name}</div>
                    <StarRating value={c.rating} readonly size={14} testIdPrefix={`comm-${i}`} />
                  </div>
                  <div className="text-[11px] text-slate-500">{c.course_name} · {new Date(c.created_at).toLocaleDateString('en-IN')}</div>
                  {c.comment && <div className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap break-words">"{c.comment}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedbackFormModal({ courses, teachers, form, onClose, onSaved }) {
  const [courseId, setCourseId] = useState(form?.course_id || '');
  const [selected, setSelected] = useState(form?.teacher_ids || []);
  const [title, setTitle] = useState(form?.title || '');
  const [description, setDescription] = useState(form?.description || 'Rate your teachers and help us improve.');
  const [isActive, setIsActive] = useState(form?.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [tSearch, setTSearch] = useState('');

  const filteredTeachers = useMemo(() => {
    const q = tSearch.toLowerCase();
    return teachers.filter((t) => !q || (t.name || '').toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q));
  }, [teachers, tSearch]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    if (!courseId) { toast.error('Pick a course'); return; }
    if (selected.length === 0) { toast.error('Select at least one teacher'); return; }
    setSaving(true);
    try {
      if (form) {
        await api.updateFeedbackForm(form.id, {
          teacher_ids: selected, title: title || null, description: description || null, is_active: isActive,
        });
        toast.success('Form updated');
      } else {
        await api.createFeedbackForm({
          course_id: courseId, teacher_ids: selected, title: title || null, description: description || null, is_active: isActive,
        });
        toast.success('Form created');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" data-testid="feedback-form-modal">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">{form ? 'Edit' : 'Create'} Feedback Form</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Course / Batch</label>
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)}
                    disabled={!!form}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100"
                    data-testid="feedback-modal-course">
              <option value="">Select a course…</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Form title (optional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="e.g. Weekly Teacher Feedback"
                   className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Instructions shown to students</label>
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">Teachers to rate</label>
              <span className="text-xs text-slate-500">{selected.length} selected</span>
            </div>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={tSearch} onChange={(e) => setTSearch(e.target.value)}
                     placeholder="Search teachers…"
                     className="w-full pl-7 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg" />
            </div>
            <div className="border border-slate-200 rounded-lg divide-y max-h-56 overflow-y-auto">
              {filteredTeachers.length === 0 && (
                <div className="p-3 text-xs text-slate-500">No teachers found</div>
              )}
              {filteredTeachers.map((t) => {
                const on = selected.includes(t.id);
                return (
                  <button key={t.id} type="button" onClick={() => toggle(t.id)}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50"
                          data-testid={`feedback-teacher-toggle-${t.id}`}>
                    <span className="truncate text-left">
                      <span className="text-slate-900">{t.name}</span>
                      <span className="text-slate-400"> · {t.email}</span>
                    </span>
                    <span className={`w-5 h-5 rounded border flex items-center justify-center ${on ? 'bg-amber-600 border-amber-600 text-white' : 'border-slate-300'}`}>
                      {on && <Check size={14} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active (public link works)
          </label>
        </div>
        <div className="p-4 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-60"
                  data-testid="feedback-modal-save-btn">
            {saving ? <Loader2 className="animate-spin" size={16} /> : null}
            {form ? 'Save changes' : 'Create form'}
          </button>
        </div>
      </div>
    </div>
  );
}
